<template>
  <v-data-table
    v-model="selected"
    :headers="headers"
    :items="prescriptions"
    :items-per-page="-1"
    item-key="id"
    show-select
    single-select
    disable-sort
    disable-filtering
    disable-pagination
    hide-default-footer
    @click:row="select"
    class="expert-tool-history__prescription-table"
  >
    <template v-slot:header[new]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon v-on="on" v-bind="attrs" class="expert-tool__table-cell-icon">mdi-flag-outline</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('general.new', { catalog: 'app_mobile'})) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:header[intensity]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            class="expert-tool__table-cell-icon"
            v-bind="attrs"
            v-on="on"
          >mdi-emoticon-dead-outline</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('expertTool.columnNames.intensity')) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:header[frequency]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            class="expert-tool__table-cell-icon"
            v-bind="attrs"
            v-on="on"
          >mdi-chart-line</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('expertTool.columnNames.frequency')) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:header[sessionDuration]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            class="expert-tool__table-cell-icon"
            v-bind="attrs"
            v-on="on"
          >mdi-timer-outline</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('expertTool.columnNames.sessionDuration')) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:header[programDuration]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            class="expert-tool__table-cell-icon"
            v-bind="attrs"
            v-on="on"
          >mdi-calendar-month-outline</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('expertTool.columnNames.programDuration')) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:header[weeksPassed]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon v-on="on" v-bind="attrs" class="expert-tool__table-cell-icon">mdi-calendar-clock</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('expertTool.columnNames.weeksPassed')) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:header[strengthTraining]>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            class="expert-tool__table-cell-icon"
            v-bind="attrs"
            v-on="on"
          >mdi-dumbbell</v-icon>
        </template>
        <span>
          {{ $ucfirst($t('pa.strengthTraining')) }}
        </span>
      </v-tooltip>
    </template>
    <template v-slot:item[new]="{ item }">
      <v-icon v-if="item.new" class="expert-tool__table-cell-icon">mdi-flag-outline</v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';

/**
 * Component HistoryPrescriptionsTable.vue
 *
 * @module HistoryPrescriptionsTable
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [deprecated] app/src/APP_DASHBOARD/templates/views/ExpertTool/History/HistoryPrescriptionsTable.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'HistoryPrescriptionsTable',
  computed: {
    ...mapGetters({
      selectedPrescriptionId: 'expertTool/selectedPrescriptionId',
      prescriptionHistory: 'expertTool/prescriptionHistory',
    }),
    headers() {
      return [
        {
          value: 'new',
          text: this.$ucfirst(this.$t('general.new', { catalog: 'app_mobile'})),
        },
        {
          value: 'date',
          text: this.$ucfirst(this.$t('alerts.date')),
        },
        {
          value: 'intensity',
          text: this.$ucfirst(this.$t('expertTool.columnNames.intensity')),
        },
        {
          value: 'frequency',
          text: this.$ucfirst(this.$t('expertTool.columnNames.frequency')),
        },
        {
          value: 'sessionDuration',
          text: this.$ucfirst(this.$t('expertTool.columnNames.sessionDuration')),
        },
        {
          value: 'programDuration',
          text: this.$ucfirst(this.$t('expertTool.columnNames.programDuration')),
        },
        {
          value: 'weeksPassed',
          text: this.$ucfirst(this.$t('expertTool.columnNames.weeksPassed')),
        },
        {
          value: 'strengthTraining',
          text: this.$ucfirst(this.$t('pa.strengthTraining')),
        },
      ];
    },
    prescriptions() {
      return this.prescriptionHistory.map((item) => {
        let intensity = [item.intensityLabel, item.heartRate ? this.$t('expertTool.heartRate.notAbove') + ' ' + item.heartRate : null].filter((item, i) => item != null);
        const startDate = moment(item.createdAt);
        const endDate = item.endDate ? moment(item.endDate) : moment();
        const numberOfWeeks = endDate.diff(startDate, 'weeks');
        return {
          id: item.id,
          new: item.new,
          date: startDate.format('DD-MM-YYYY'),
          intensity: intensity.join(' - '),
          frequency: item.frequencyLabel,
          sessionDuration: item.sessionDurationLabel,
          programDuration: item.programDurationLabel,
          weeksPassed: this.$t('journeyCaseNurseAlerts.numberOfWeeks', {catalog: 'messages', params: {numberOfWeeks}}),
          strengthTraining: item.strengthTrainingLabel,
        };
      });
    },
    selected: {
      get() {
        return this.selectedPrescriptionId
          ? [{ id: this.selectedPrescriptionId }] : [];
      },
      set(selected) {
        if (selected && selected.length && selected[0].id !== this.selectedPrescriptionId) {
          this.$store.dispatch(
            'expertTool/setHistorySelectedPrescriptionId',
            selected[0].id,
          );
        }
      },
    },
  },
  methods: {
    select({ id }) {
      if (id === this.selectedPrescriptionId) {
        this.$store.dispatch('expertTool/setHistorySelectedPrescriptionId', '');
      } else {
        this.$store.dispatch('expertTool/setHistorySelectedPrescriptionId', id);
      }
    },
  },
};
</script>

<style lang="scss">
.expert-tool-history__prescription-table {
  width: 100%;
}

.expert-tool-history__prescription-table tr {
  cursor: pointer;
}

.expert-tool-history__prescription-table th,
.expert-tool-history__prescription-table td {
  font-size: 1rem !important;
}
</style>
